import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import styled from 'styled-components'
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import Text from '../atoms/Text';
import { formatTime } from '../../utils/formatTime';

const Container = styled.div`
  width: 100%;
  padding: 20px;
  background: ${(props) => props.theme.colors.neutral[200]};
  display: flex;
  align-items: center;
`
const PlayButton = styled.button`
  margin-right: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
`
const PauseIcon = styled(PauseRoundedIcon)`
  color: ${(props) => props.theme.colors.secondary};
`
const PlayIcon = styled(PlayArrowRoundedIcon)`
  color: ${(props) => props.theme.colors.secondary};
`
const ProgressWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
`
const ProgressBar = styled.div`
  position: relative;
  height: 6px;
  width: 100%;
  background: ${(props) => props.theme.colors.neutral[400]};
  border-radius: 4px;
`;

const ProgressFill = styled.div`
  position: absolute;
  height: 100%;
  left: 0;
  top: 0;
  background: ${(props) => props.theme.colors.secondary};
  width: ${(props) => props.$percent}%;
  border-radius: 4px;
`;

const Thumb = styled.div`
  position: absolute;
  top: 50%;
  left: ${(props) => props.$percent}%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(props) => props.theme.colors.secondary};
  touch-action: none;
  user-select: none;
  -webkit-user-drag: none;`;

// forwardRef: 부모 컴포넌트로부터 ref를 전달받기 위해 자식 컴포넌트에서 forwardRef 선언
const AudioPlayer = forwardRef(({ src }, ref) => {
  const audioRef = useRef(null);
  const barRef = useRef(null);

  const isDraggingRef = useRef(false); // 드래그 여부
  const wasPlayingRef = useRef(false); // 프로그레스바 드래그 전 재생 여부

  const [isPlaying, setIsPlaying] = useState(false); // 재생 여부(UI용)
  const [currentTime, setCurrentTime] = useState(0); // 현재 시간
  const [duration, setDuration] = useState(0); // 총 녹음 시간


  // 재생 및 타임스탬프 버튼 누르면 오디오 재생 지점 재설정
  // useImperativeHandle: 부모 컴포넌트에서 자식 컴포넌트의 내부 함수나 값에 접근할 수 있게 해주는 기능을 제공하는 react hook, 자식 컴포넌트에서 선언
  useImperativeHandle(ref, () => ({
    seekToTime: (timeInSeconds) => {
      if (audioRef.current) {
        audioRef.current.currentTime = timeInSeconds;
        setCurrentTime(timeInSeconds);
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  }));

  // 재생 및 일시정지
  const togglePlay = async() => {
    const audio = audioRef.current;
    if (!audio) return;

    // ended 직후 바로 재생 안되는 문제 방지
    if (audio.ended) {
      audio.currentTime = 0;
    }

     if (audio.paused) {
      try {
        await audio.play();
        setIsPlaying(true);
      } catch {}
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const seekTo = (clientX) => {
    const audio = audioRef.current;
    const bar = barRef.current;
    if (!audio || !bar) return;

    // element의 크기 및 위치 정보
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    const time = ratio * duration;

    audio.currentTime = time;
    setCurrentTime(time);
  };

  const handlePointerDown = (e) => {
    const audio = audioRef.current;
    if (!audio) return;

    e.preventDefault();
    barRef.current.setPointerCapture(e.pointerId);

    wasPlayingRef.current = !audio.paused;
    isDraggingRef.current = true;

    audio.pause(); // 소리만 멈추기
    seekTo(e.clientX);
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return;
    seekTo(e.clientX);
  };

  const handlePointerUp = (e) => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;

    barRef.current.releasePointerCapture(e.pointerId);

    if (wasPlayingRef.current) {
      requestAnimationFrame(() => {
        audio.play();
      });
    }
  };

  // 오디오 이벤트
  useEffect(() => {
    // audioRef.current 연결되지 않았을 때 에러 방지
    const audio = audioRef.current;
    if (!audio) return;
    
    // audio.duration(총 녹음 시간) 저장
    const onLoaded = () => setDuration(audio.duration);
    
    // 오디오가 재생중일 때 현재 시간 반영
    const onTimeUpdate = () => {
      if (!isDraggingRef.current) {
        setCurrentTime(audio.currentTime);
      }
    };

    // 오디오 재생이 끝났을 때 UI 초기화
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    // 이벤트 리스너 등록
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);

    return () => {
      // 이벤트 리스너 해제
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  
  const percent = duration ? (currentTime / duration) * 100 : 0;

  // function formatTime(sec) {
  //   if (!sec) return "00:00";
  //   const mm = Math.floor(sec / 60);
  //   const ss = Math.floor(sec % 60);
  //   return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  // }

  /* src가 준비되지 않았을 때 에러 방지
     (the element has no supported sources.) */
  if (!src) return null;

  return (
    <Container>
      <audio ref={audioRef} src={src} />

      <PlayButton onClick={togglePlay}>
        {isPlaying ? <PauseIcon sx={{ fontSize: 36 }}/> : <PlayIcon sx={{ fontSize: 36 }}/>}
      </PlayButton>
      <ProgressWrapper>
        <Text variant="body2" weight="medium">{formatTime(currentTime)}</Text>
        <ProgressBar
          ref={barRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <ProgressFill $percent={percent} />
          <Thumb $percent={percent} />
        </ProgressBar>
        <Text variant="body2" weight="medium">{formatTime(duration)}</Text>   
      </ProgressWrapper>
    </Container>
  );
});

export default AudioPlayer